import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function OnboardingTab({ user }) {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeProgress, setEmployeeProgress] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    category: 'all',
    pinned: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch items
      const itemsRes = await fetch(`${API_BASE}/api/admin/onboarding/items`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!itemsRes.ok) throw new Error('Failed to fetch items');
      const itemsData = await itemsRes.json();
      setItems(itemsData.items || []);

      // Fetch progress
      const progressRes = await fetch(`${API_BASE}/api/admin/onboarding/progress`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!progressRes.ok) throw new Error('Failed to fetch progress');
      const progressData = await progressRes.json();
      setProgress(progressData.progress || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeProgress = async (employeeEmail) => {
    setEmployeeLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/employee/${encodeURIComponent(employeeEmail)}/progress`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch employee progress');
      const data = await res.json();
      setEmployeeProgress(data);
    } catch (error) {
      console.error('Error fetching employee progress:', error);
      alert(error.message || 'Error fetching employee progress');
    } finally {
      setEmployeeLoading(false);
    }
  };

  const updateItemStatus = async (employeeEmail, itemId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/employee/${encodeURIComponent(employeeEmail)}/item/${itemId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) throw new Error('Failed to update item status');
      
      // Refresh employee progress
      await fetchEmployeeProgress(employeeEmail);
      
      // Refresh overall progress data
      fetchData();
      
      return true;
    } catch (error) {
      console.error('Error updating item status:', error);
      alert(error.message || 'Error updating item status');
      return false;
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Please fill in title and description');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setItems([...items, data.item]);
        setFormData({ title: '', description: '', link: '', category: 'all', pinned: false });
        setShowAddModal(false);
        // Refresh progress data
        fetchData();
      } else {
        throw new Error(data.error || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      alert(error.message || 'Error adding item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Please fill in title and description');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/items/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setItems(items.map(item => 
          item.id === editingItem.id ? data.item : item
        ));
        setFormData({ title: '', description: '', link: '', category: 'all', pinned: false });
        setShowEditModal(false);
        setEditingItem(null);
      } else {
        throw new Error(data.error || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert(error.message || 'Error updating item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title}"? This will also remove all employee progress for this item.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/items/${item.id}`, {
        method: 'DELETE',
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setItems(items.filter(i => i.id !== item.id));
        // Refresh progress data
        fetchData();
      } else {
        throw new Error(data.error || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(error.message || 'Error deleting item');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      link: item.link || '',
      category: item.category || 'all',
      pinned: !!item.pinned
    });
    setShowEditModal(true);
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
    fetchEmployeeProgress(employee.email);
  };

  if (loading) {
    return <div>Loading onboarding data...</div>;
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Onboarding Management</h2>
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <button 
            className="primary-button"
            onClick={() => setShowAddModal(true)}
          >
            Add Onboarding Item
          </button>
        </div>
      </div>

      {/* Onboarding Items Section */}
      <div className="section-container">
        <h3>Onboarding Items ({items.length})</h3>
        
        {items.length === 0 ? (
          <p className="empty-state">No onboarding items configured. Add one to get started!</p>
        ) : (
          <div className="onboarding-admin-items">
            {items.map((item) => (
              <div key={item.id} className="onboarding-admin-item">
                <div className="item-info">
                  <h4>
                    {item.title}
                    {item.pinned && (
                      <span className="badge badge-info" style={{ marginLeft: '8px' }}>Pinned</span>
                    )}
                    {item.category && item.category !== 'all' && (
                      <span className="category-badge" style={{ marginLeft: '8px' }}>
                        {item.category}
                      </span>
                    )}
                  </h4>
                  <p>{item.description}</p>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="item-link">
                      {item.link}
                    </a>
                  )}
                </div>
                <div className="item-actions">
                  <button 
                    className="secondary-button"
                    onClick={() => openEditModal(item)}
                  >
                    Edit
                  </button>
                  <button 
                    className="danger-button"
                    onClick={() => handleDeleteItem(item)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Employee Progress Section */}
      <div className="section-container" style={{ marginTop: '32px' }}>
        <h3>Employee Progress</h3>
        
        {progress.length === 0 ? (
          <p className="empty-state">No employee progress data available.</p>
        ) : (
          <div className="progress-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Completion</th>
                </tr>
              </thead>
              <tbody>
                {progress.map((emp) => (
                  <tr 
                    key={emp.email}
                    onClick={() => handleEmployeeClick(emp)}
                    style={{ cursor: 'pointer' }}
                    className="clickable-row"
                  >
                    <td>
                      <div>{emp.name || emp.email}</div>
                      <div className="text-small text-muted">{emp.email}</div>
                    </td>
                    <td>
                      {emp.is_pre_migration ? (
                        <span className="badge badge-success">Pre-migration</span>
                      ) : (
                        <span className="badge badge-info">New employee</span>
                      )}
                    </td>
                    <td>
                      <div className="progress-bar-container small">
                        <div 
                          className="progress-bar-fill"
                          style={{ width: `${emp.completion_percentage}%` }}
                        />
                      </div>
                    </td>
                    <td>
                      {emp.completed_items} / {emp.total_items} ({Math.round(emp.completion_percentage)}%)
                      <span className="text-small text-muted" style={{ marginLeft: '8px' }}>
                        Click to manage →
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
        <h2>Add Onboarding Item</h2>
        
        <form onSubmit={handleAddItem}>
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Complete Slack Setup"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Join the company Slack workspace and set up your profile"
              rows={3}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              <option value="all">All Employees</option>
              <option value="US">US Employees Only</option>
              <option value="UK">UK Employees Only</option>
              <option value="AUS">Australia Employees Only</option>
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              id="pinned"
              type="checkbox"
              checked={!!formData.pinned}
              onChange={(e) => setFormData({ ...formData, pinned: e.target.checked })}
            />
            <label htmlFor="pinned">Pin to top</label>
          </div>

          <div className="form-group">
            <label htmlFor="link">Link (optional)</label>
            <input
              id="link"
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              placeholder="e.g., https://symbolica.slack.com/signup"
            />
          </div>

          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button 
              type="button" 
              className="secondary-button"
              onClick={() => setShowAddModal(false)}
              disabled={submitting}
              style={{ marginRight: 8 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="primary-button"
              disabled={submitting}
            >
              {submitting ? 'Adding...' : 'Add Item'}
            </button>
          </div>
        </form>
      </Modal>
             
      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <h2>Edit Onboarding Item</h2>
        
        <form onSubmit={handleUpdateItem}>
          <div className="form-group">
            <label htmlFor="edit-title">Title *</label>
            <input
              id="edit-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-description">Description *</label>
            <textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-category">Category *</label>
            <select
              id="edit-category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              <option value="all">All Employees</option>
              <option value="US">US Employees Only</option>
              <option value="UK">UK Employees Only</option>
              <option value="AUS">Australia Employees Only</option>
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              id="edit-pinned"
              type="checkbox"
              checked={!!formData.pinned}
              onChange={(e) => setFormData({ ...formData, pinned: e.target.checked })}
            />
            <label htmlFor="edit-pinned">Pin to top</label>
          </div>

          <div className="form-group">
            <label htmlFor="edit-link">Link (optional)</label>
            <input
              id="edit-link"
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            />
          </div>

          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button 
              type="button" 
              className="secondary-button"
              onClick={() => {
                setShowEditModal(false);
                setEditingItem(null);
              }}
              disabled={submitting}
              style={{ marginRight: 8 }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="primary-button"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Item'}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Employee Detail Modal */}
      <Modal 
        isOpen={showEmployeeModal} 
        onClose={() => {
          setShowEmployeeModal(false);
          setSelectedEmployee(null);
          setEmployeeProgress(null);
        }}
        style={{ minWidth: '600px' }}
      >
        <h2>Manage Employee Onboarding</h2>
        
        {selectedEmployee && (
          <div className="employee-info" style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>{selectedEmployee.name || selectedEmployee.email}</h3>
            <p style={{ margin: '0', color: '#666' }}>
              {selectedEmployee.email} • 
              {selectedEmployee.is_pre_migration ? (
                <span className="badge badge-success" style={{ marginLeft: '8px' }}>Pre-migration</span>
              ) : (
                <span className="badge badge-info" style={{ marginLeft: '8px' }}>New employee</span>
              )}
            </p>
            <div style={{ marginTop: '8px' }}>
              Progress: {selectedEmployee.completed_items} / {selectedEmployee.total_items} ({Math.round(selectedEmployee.completion_percentage)}%)
            </div>
          </div>
        )}

        {employeeLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Loading employee details...
          </div>
        ) : employeeProgress ? (
          <div>
            <h4>Onboarding Items</h4>
            {employeeProgress.items && employeeProgress.items.length > 0 ? (
              <div className="employee-items-list">
                {employeeProgress.items.map((item) => (
                  <div key={item.id} className="employee-item" style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '16px', 
                    marginBottom: '12px',
                    backgroundColor: '#fff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h5 style={{ margin: '0 0 8px 0' }}>
                          {item.title}
                          {item.category && item.category !== 'all' && (
                            <span className="category-badge" style={{ marginLeft: '8px', fontSize: '12px' }}>
                              {item.category}
                            </span>
                          )}
                        </h5>
                        <p style={{ margin: '0 0 12px 0', color: '#666' }}>{item.description}</p>
                        {item.link && (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="item-link" style={{ fontSize: '14px' }}>
                            {item.link}
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: '500', fontSize: '14px' }}>Status:</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className={`status-button ${item.progress?.status === 'completed' ? 'active completed' : ''}`}
                            onClick={() => updateItemStatus(selectedEmployee.email, item.id, 'completed')}
                            style={{
                              padding: '4px 12px',
                              border: '1px solid #28a745',
                              borderRadius: '4px',
                              backgroundColor: item.progress?.status === 'completed' ? '#28a745' : 'transparent',
                              color: item.progress?.status === 'completed' ? '#fff' : '#28a745',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✓ Complete
                          </button>
                          <button
                            className={`status-button ${item.progress?.status === 'incomplete' ? 'active incomplete' : ''}`}
                            onClick={() => updateItemStatus(selectedEmployee.email, item.id, 'incomplete')}
                            style={{
                              padding: '4px 12px',
                              border: '1px solid #dc3545',
                              borderRadius: '4px',
                              backgroundColor: item.progress?.status === 'incomplete' ? '#dc3545' : 'transparent',
                              color: item.progress?.status === 'incomplete' ? '#fff' : '#dc3545',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✗ Incomplete
                          </button>
                          <button
                            className={`status-button ${item.progress?.status === 'na' ? 'active na' : ''}`}
                            onClick={() => updateItemStatus(selectedEmployee.email, item.id, 'na')}
                            style={{
                              padding: '4px 12px',
                              border: '1px solid #6c757d',
                              borderRadius: '4px',
                              backgroundColor: item.progress?.status === 'na' ? '#6c757d' : 'transparent',
                              color: item.progress?.status === 'na' ? '#fff' : '#6c757d',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            − N/A
                          </button>
                        </div>
                      </div>
                      {item.progress?.updated_at && (
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                          Last updated: {new Date(item.progress.updated_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No onboarding items assigned to this employee.</p>
            )}
            
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button 
                className="primary-button"
                onClick={() => {
                  setShowEmployeeModal(false);
                  setSelectedEmployee(null);
                  setEmployeeProgress(null);
                }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            Failed to load employee details.
          </div>
        )}
      </Modal>
    </div>
  );
} 